import React, { Component } from 'react'
import axios from 'axios'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { push, replace } from 'connected-react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Menu, MenuItem, Popover, Spinner } from '@blueprintjs/core'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown'
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser'

import ImpersonateUser from './ImpersonateUser'
import withUserData from 'src/HOCs/withUserData'
import ImpersonateOrganization from './ImpersonateOrganization'
import { UserDataContext } from '../../HOCs/withAuth'
import { TOAST_SHOW } from 'src/constants/actionTypes'
import { bindActionCreators } from 'redux'
import { initApp } from '../../store/modules/app'
import { LOGOUT_PATH } from '../../constants/paths'
import styles from './userMenu.module.scss'

class UserMenu extends Component {
    state = {
        users: [],
        organizations: [],
        loadingUsers: false,
        loadingOrganizations: false,
        loadImpersonation: false,
    }

    static contextType = UserDataContext

    getPermissions(username, organization, headers = {}) {
        let params = {}
        if (organization) {
            params.organization_id = organization
        }
        return axios.get(`/api/auth/users/${username}/app-permissions`, {
            params,
            headers: { ...axios.defaults.headers, ...headers },
        })
    }

    /**
     * This is used to refresh the data once a user/organization is impersonated
     */
    refreshPage() {
        this.props.actions.initApp()
        const { dispatch, history } = this.props
        const currentLocation = history.location.pathname

        dispatch(push('/'))
        dispatch(replace(currentLocation))
    }

    handleLoadUsers = (userData, searchTerm) => {
        if (userData.is_global_admin && !userData.is_local_user) {
            if (searchTerm === undefined) {
                this.setState({ loadingUsers: true })
            }

            const params = {
                per_page: 20,
                sort: '+username',
            }

            if (searchTerm) {
                params.first_name_like = searchTerm
                params.last_name_like = searchTerm
                params.email_like = searchTerm
                params.use_or = true
            }

            return axios
                .get(`/api/auth/users`, {
                    params,
                })
                .then((response) => {
                    if (response.data) {
                        this.setState({ users: response.data.results })
                    }
                })
                .catch((ex) => {
                    this.setState({ users: [] })
                })
                .finally(() => {
                    this.setState({ loadingUsers: false })
                })
        }
    }

    handleLoadOrganizations = (username) => {
        this.setState({ loadingOrganizations: true })

        if (!this.context.data.is_local_user) {
            return axios
                .get(`/api/auth/users/${username}/tenants`)
                .then((response) => {
                    if (response.data) {
                        this.setState({
                            organizations: response.data.tenants,
                        })
                    }
                })
                .catch((err) => {
                    console.error(err)
                    this.setState({ organizations: [] })
                })
                .finally(() => {
                    this.setState({ loadingOrganizations: false })
                })
        }
    }

    handleUserChange = (chosen, callback) => {
        if (!chosen) {
            //Stop impersonating
            axios
                .delete('/api/auth/impersonator')
                .then((response) => {
                    const data = {
                        is_global_admin: response.data.global_admin,
                        firstName: response.data.first_name,
                        lastName: response.data.last_name,
                        is_local_user: false,
                        username: response.data.username,
                        email: response.data.email,
                    }
                    let permissions = null
                    this.getPermissions(data.username, null, {
                        Authorization: response.headers.authorization,
                    })
                        .then((permissionResponse) => {
                            const { app_permissions } = permissionResponse.data
                            if (
                                !app_permissions ||
                                app_permissions.length === 0
                            ) {
                                permissions = []
                            } else {
                                permissions = app_permissions[0].actions
                            }
                            this.context.setImpersonatedUser(
                                response.headers.authorization,
                                null,
                                data,
                                null,
                                permissions
                            )

                            if (typeof callback === 'function') callback()

                            this.refreshPage()
                        })
                        .catch((e) => {
                            console.error(e)
                        })
                })
                .catch((e) => {
                    console.error(e)
                })
        } else {
            this.setState({ loadImpersonation: true })

            // Impersonate user
            axios
                .post('/api/auth/impersonator', {
                    sub: chosen.value,
                })
                .then((response) => {
                    const data = {
                        is_global_admin: response.data.global_admin,
                        firstName: response.data.first_name,
                        lastName: response.data.last_name,
                        is_local_user: false,
                        username: response.data.username,
                        email: response.data.email,
                    }
                    let permissions = null
                    this.getPermissions(chosen.value, null, {
                        Authorization: response.headers.authorization,
                    })
                        .then((permissionResponse) => {
                            const { app_permissions } = permissionResponse.data
                            if (
                                !app_permissions ||
                                app_permissions.length === 0
                            ) {
                                permissions = []
                            } else {
                                permissions = app_permissions[0].actions
                            }
                            this.context.setImpersonatedUser(
                                response.headers.authorization,
                                chosen,
                                data,
                                null,
                                permissions
                            )

                            if (typeof callback === 'function') callback()

                            this.refreshPage()
                        })
                        .catch((e) => {
                            console.error(e)
                            this.handleImpersonateError(chosen)
                        })
                })
                .catch((e) => {
                    console.error(e)
                    this.handleImpersonateError(chosen)
                })
                .finally(() => {
                    this.setState({ loadImpersonation: false })
                })
        }
    }

    handleOrganizationChange = (chosen) => {
        if (!chosen) {
            axios
                .delete('/api/auth/impersonator', {
                    params: {
                        organization_only: true,
                    },
                })
                .then((response) => {
                    let permissions = null
                    this.getPermissions(this.context.data.username, null)
                        .then((permissionResponse) => {
                            const { app_permissions } = permissionResponse.data
                            if (
                                !app_permissions ||
                                app_permissions.length === 0
                            ) {
                                permissions = []
                            } else {
                                permissions = app_permissions[0].actions
                            }
                            this.context.setImpersonatedOrganization(
                                response.headers.authorization,
                                null,
                                permissions
                            )

                            this.refreshPage()
                        })
                        .catch((e) => {
                            console.error(e)
                        })
                })
                .catch((e) => {
                    console.error(e)
                })
        } else {
            this.setState({ loadImpersonation: true })

            axios
                .post('/api/auth/impersonator', {
                    organization_id: chosen.value,
                })
                .then((response) => {
                    let permissions = null
                    this.getPermissions(
                        this.context.data.username,
                        chosen.value
                    )
                        .then((permissionResponse) => {
                            const { app_permissions } = permissionResponse.data
                            if (
                                !app_permissions ||
                                app_permissions.length === 0
                            ) {
                                permissions = []
                            } else {
                                permissions = app_permissions[0].actions
                            }
                            this.context.setImpersonatedOrganization(
                                response.headers.authorization,
                                chosen,
                                permissions
                            )

                            this.refreshPage()
                        })
                        .catch((e) => {
                            console.error(e)
                            this.handleImpersonateError(chosen)
                        })
                })
                .catch((e) => {
                    console.error(e)
                    this.handleImpersonateError(chosen)
                })
                .finally(() => {
                    this.setState({ loadImpersonation: false })
                })
        }
    }

    handleImpersonateError = (chosen) => {
        const { dispatch } = this.props

        dispatch({
            type: TOAST_SHOW,
            message: {
                content: `Unable to impersonate ${chosen.label}`,
                intent: 'warning',
            },
        })
    }

    render() {
        const {
            users,
            loadingUsers,
            organizations,
            loadingOrganizations,
            loadImpersonation,
        } = this.state
        const { userData } = this.props
        const { firstName, lastName } = userData.data
        return (
            <>
                <Popover
                    minimal
                    position="bottom-right"
                    usePortal={false}
                    disabled={loadImpersonation}
                    content={
                        <Menu>
                            <div className={styles.impersonateContainer}>
                                <ImpersonateOrganization
                                    organizations={organizations}
                                    loadingOrganizations={loadingOrganizations}
                                    onLoadOrganizations={
                                        this.handleLoadOrganizations
                                    }
                                    onOrganizationChange={
                                        this.handleOrganizationChange
                                    }
                                />

                                <ImpersonateUser
                                    users={users}
                                    loadingUsers={loadingUsers}
                                    onLoadUsers={this.handleLoadUsers}
                                    onLoadOrganizations={
                                        this.handleLoadOrganizations
                                    }
                                    onUserChange={this.handleUserChange}
                                />
                            </div>
                            <MenuItem
                                text="Logout"
                                onClick={() =>
                                    this.props.dispatch(push(LOGOUT_PATH))
                                }
                            />
                        </Menu>
                    }
                    className={styles.popover}
                >
                    <Button className={styles.userMenu} minimal>
                        <FontAwesomeIcon
                            className={styles.leftIcon}
                            icon={faUser}
                        />

                        <div>
                            <p
                                className={styles.name}
                            >{`${firstName} ${lastName}`}</p>
                            <p className={styles.subtitle}>
                                {userData.impersonatedOrganization
                                    ? userData.impersonatedOrganization.label
                                    : 'Admin profile'}
                            </p>
                        </div>

                        {loadImpersonation ? (
                            <Spinner
                                intent="primary"
                                size={16}
                                className={styles.loadSpinner}
                            />
                        ) : (
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={styles.rightIcon}
                            />
                        )}
                    </Button>
                </Popover>
            </>
        )
    }
}

const mapDispatchToProps = (dispatch) => ({
    dispatch,
    actions: bindActionCreators(
        {
            initApp,
        },
        dispatch
    ),
})

export default connect(
    null,
    mapDispatchToProps
)(withUserData(withRouter(UserMenu)))
